import { Component, Inject, OnInit, OnDestroy } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { thru } from "lodash";
import { CorporateService } from "../corporate.service";

@Component({
  selector: "app-corporate-workorder-details",
  templateUrl: "./corporate-workorder-details.component.html",
  styleUrls: ["./corporate-workorder-details.component.css"],
})
export class CorporateWorkorderDetailsComponent implements OnInit, OnDestroy {
  ModelData: any;
  token: any;
  WODetails: any = [];
  loading: boolean = false;
  selectedCategory: any;
  bidRankingData: any[] = [];
  hasBidVisibility: boolean = false;
  
  // Timer properties
  intervalId: any;
  timerHours: string = '00';
  timerMinutes: string = '00';
  timerSeconds: string = '00';
  isTimerExpired: boolean = true;
  isBidRankingLoaded: boolean = false;
  isBidRankingForbidden: boolean = false;
  
  // Edit Price properties
  isEditingPrice: boolean = false;
  newPriceValue: number;
  isUpdatingPrice: boolean = false;
  refreshIntervalId: any;
  showConfirmTripModal: boolean = false;

  constructor(
    private matdialogref: MatDialogRef<CorporateWorkorderDetailsComponent>,
    @Inject(MAT_DIALOG_DATA) data: any,
    private services: CorporateService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {
    this.ModelData = data;
  }

  ngOnInit(): void {
    this.token = sessionStorage.getItem("corporate_token");
    if (this.token == null) {
      this.router.navigateByUrl("/");
    }
    if (this.ModelData._id) {
      this.workOrderDetails();
      
      // Auto refresh every 30 seconds
      this.refreshIntervalId = setInterval(() => {
        this.fetchBidRanking();
      }, 30000);
    }
  }

  close() {
    this.matdialogref.close();
  }
  workOrderDetails() {
    this.loading = false;

    this.services.workOrderView(this.token, this.ModelData._id).subscribe(
      (response: any) => {
        this.WODetails = response.body.data;
        this.loading = false;
        
        // Setup initial price value
        this.newPriceValue = this.WODetails[0]?.price_per_ton;

        this.fetchBidRanking();
        this.selectedCategory =
          this.WODetails[0]?.pay_as_load_quantity === "unloaded_quantity"
            ? this.WODetails[0].tripInfo.workOrderOtherDetails
                ?.unloading_point || this.WODetails[0].workorder_qty
            : this.WODetails[0].workOrderOtherDetails?.loading_point ||
              this.WODetails[0].workorder_qty;
      },
      (error) => {
        if (error.error.message === "jwt malformed") {
          this.router.navigateByUrl("/");
          window.localStorage.clear();
        }
        if (error.error.message === "jwt expired") {
          this.router.navigateByUrl("/");
          window.localStorage.clear();
        }
        this.snackBar.open(error.error.message, "Fail", {
          duration: 5000,
        });
      }
    );
  }
  help() {
    this.close();
    this.router.navigateByUrl("/?target=support");
  }

  fetchBidRanking() {
    this.services.getBidRateRanking(this.ModelData._id, this.token).subscribe(
      (response: any) => {
        this.isBidRankingLoaded = true;
        this.isBidRankingForbidden = false;
        this.hasBidVisibility = response.body ? (response.body.bid_visibility !== false) : true;
        if (response.body && response.body.data) {
          this.bidRankingData = response.body.data;
          
          // Setup timer from bidRanking response if available
          if (this.bidRankingData.length > 0 && this.bidRankingData[0].workOrderDetails?.workorder_expiry_time) {
            const expiryTime = Number(this.bidRankingData[0].workOrderDetails.workorder_expiry_time);
            const timeDifference = expiryTime - Date.now();
            this.startCountdown(timeDifference);
          } else if (this.WODetails[0]?.workorder_expiry_time) {
            // Fallback to WODetails if no bids yet
            const expiryTime = Number(this.WODetails[0].workorder_expiry_time);
            const timeDifference = expiryTime - Date.now();
            this.startCountdown(timeDifference);
          } else {
            this.isTimerExpired = true;
          }
        } else if (this.WODetails[0]?.workorder_expiry_time) {
          // Fallback if empty response
          const expiryTime = Number(this.WODetails[0].workorder_expiry_time);
          const timeDifference = expiryTime - Date.now();
          this.startCountdown(timeDifference);
        } else {
          this.isTimerExpired = true;
        }
      },
      (error) => {
        this.isBidRankingLoaded = true;
        console.error('Error fetching bid ranking', error);
        if (error.status === 403) {
          this.hasBidVisibility = false;
          this.isBidRankingForbidden = true;
          this.bidRankingData = [];
          if (this.refreshIntervalId) {
            clearInterval(this.refreshIntervalId);
          }
        }
        // Fallback on error
        if (this.WODetails[0]?.workorder_expiry_time) {
          const expiryTime = Number(this.WODetails[0].workorder_expiry_time);
          const timeDifference = expiryTime - Date.now();
          this.startCountdown(timeDifference);
        } else {
          this.isTimerExpired = true;
        }
      }
    );
  }

  closeBidding() {
    this.services.closeBidding(this.ModelData._id, this.token).subscribe(
      (response: any) => {
        if (response.status === 200) {
          this.snackBar.open('Bidding closed successfully', 'Success', { duration: 3000 });
          this.workOrderDetails(); // Refresh
        }
      },
      (error) => {
        this.snackBar.open(error.error?.message || 'Failed to close bidding', 'Fail', { duration: 5000 });
      }
    );
  }

  startCountdown(timeDifference: number) {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    
    if (!timeDifference || isNaN(timeDifference) || timeDifference <= 0) {
      this.isTimerExpired = true;
      this.timerHours = '00';
      this.timerMinutes = '00';
      this.timerSeconds = '00';
      if (this.refreshIntervalId) {
        clearInterval(this.refreshIntervalId);
      }
      return;
    }
    
    this.isTimerExpired = false;
    const endTime = Date.now() + timeDifference;

    this.intervalId = setInterval(() => {
      const currentTime = Date.now();
      const remainingTime = endTime - currentTime;

      if (remainingTime <= 0) {
        clearInterval(this.intervalId);
        if (this.refreshIntervalId) {
          clearInterval(this.refreshIntervalId);
        }
        this.isTimerExpired = true;
        this.timerHours = '00';
        this.timerMinutes = '00';
        this.timerSeconds = '00';
        this.workOrderDetails(); // Auto refresh when expired
        return;
      }

      const totalSeconds = Math.floor(remainingTime / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.timerHours = hours < 10 ? '0' + hours : hours.toString();
      this.timerMinutes = minutes < 10 ? '0' + minutes : minutes.toString();
      this.timerSeconds = seconds < 10 ? '0' + seconds : seconds.toString();
    }, 1000);
  }

  ngOnDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
    }
  }

  toggleEditPrice() {
    this.isEditingPrice = !this.isEditingPrice;
    if (this.isEditingPrice) {
      this.newPriceValue = this.WODetails[0]?.price_per_ton;
    }
  }

  submitNewPrice() {
    if (this.newPriceValue && this.newPriceValue > 0) {
      this.isUpdatingPrice = true;
      this.services.editPrice(this.ModelData._id, this.newPriceValue, this.token).subscribe(
        (response: any) => {
          this.isUpdatingPrice = false;
          if (response.status === 200) {
            this.snackBar.open('Price updated successfully', 'Success', { duration: 3000 });
            this.isEditingPrice = false;
            this.workOrderDetails();
          }
        },
        (error) => {
          this.isUpdatingPrice = false;
          this.snackBar.open(error.error?.message || 'Failed to update price', 'Fail', { duration: 5000 });
        }
      );
    } else {
      this.snackBar.open('Please enter a valid price', 'Fail', { duration: 3000 });
    }
  }

  openConfirmModal() {
    this.showConfirmTripModal = true;
  }

  closeConfirmModal() {
    this.showConfirmTripModal = false;
  }
}
